import type { ItemTimeline } from '@baiocchi/shared'
import {
  Botao,
  cn,
  ehUrlExterna,
  EstadoVazio,
  Esqueleto,
  Etiqueta,
  ItemLinhaDoTempo,
  LinhaDoTempo,
  LinkExterno,
} from '@baiocchi/ui'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { chaves } from '../../api/chaves'
import { leadsApi } from '../../api/leads'
import { EstadoDeErro } from '../../componentes/estados-tela'
import {
  IconeAgenda,
  IconeAlerta,
  IconeArquivo,
  IconeCheck,
  IconeEditar,
  IconeMais,
  IconeTransferir,
  IconeWhatsapp,
  IconeX,
} from '../../componentes/icones'
import { TextoComLinks } from '../../componentes/texto-com-links'
import { ROTULO_TIPO_INTERACAO, TOM_TIPO_INTERACAO } from '../../dominio/rotulos'
import { ROTULO_STATUS_LEAD } from '@baiocchi/shared'
import { formatarDataHora } from '../../utilitarios/formatar'

const ICONES = {
  CRIACAO: IconeMais,
  ATENDIMENTO_MANUAL: IconeEditar,
  MUDANCA_STATUS: IconeCheck,
  TRANSFERENCIA: IconeTransferir,
  AGENDAMENTO_CRIADO: IconeAgenda,
  AGENDAMENTO_CONCLUIDO: IconeCheck,
  AGENDAMENTO_CANCELADO: IconeX,
  PROPOSTA_CRIADA: IconeArquivo,
  IMPORTACAO: IconeArquivo,
  INTEGRACAO_RECEBIDA: IconeArquivo,
  WHATSAPP_ABERTO: IconeWhatsapp,
  FINALIZACAO_AUTOMATICA: IconeAlerta,
  AVALIACAO_RECEBIDA: IconeAlerta,
} as const

/**
 * Aba "Interação do Lead" — timeline cronológica, mais recente no topo.
 *
 * **Nenhum item tem ação de editar ou excluir.** Não é escopo cortado: a timeline
 * é append-only por trigger no banco, e RN-25 vale para todos os papéis, inclusive
 * Administrador. Correção se faz com uma nova interação.
 *
 * Paginação por cursor (padrão 20), carregada por "Carregar mais" em vez de
 * rolagem infinita: a timeline fica ao lado de um formulário, e rolagem infinita
 * dentro de coluna disputa o gesto de rolar a página.
 */
export function TimelineLead({ leadUuid }: { leadUuid: string }) {
  const consulta = useInfiniteQuery({
    queryKey: chaves.leads.timeline(leadUuid),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      leadsApi.timeline(leadUuid, { limite: 20, cursor: pageParam }, signal),
    getNextPageParam: (ultima) => ultima.paginacao.proximoCursor ?? undefined,
    staleTime: 10_000,
  })

  if (consulta.isPending) {
    return (
      <div className="space-y-4 p-4">
        {Array.from({ length: 4 }, (_, indice) => (
          <Esqueleto key={indice} className="h-20 w-full" />
        ))}
      </div>
    )
  }

  if (consulta.isError) {
    return <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
  }

  const itens = consulta.data.pages.flatMap((pagina) => pagina.dados)

  if (itens.length === 0) {
    return (
      <EstadoVazio
        titulo="Nenhuma interação registrada"
        descricao="Registre o primeiro atendimento no formulário ao lado."
      />
    )
  }

  return (
    <div className="p-4">
      <LinhaDoTempo>
        {itens.map((item) => (
          <ItemDaTimeline key={item.id} item={item} />
        ))}
      </LinhaDoTempo>

      {consulta.hasNextPage && (
        <div className="mt-2 flex justify-center">
          <Botao
            variante="contorno"
            tamanho="sm"
            carregando={consulta.isFetchingNextPage}
            onClick={() => void consulta.fetchNextPage()}
          >
            Carregar interações anteriores
          </Botao>
        </div>
      )}
    </div>
  )
}

function ItemDaTimeline({ item }: { item: ItemTimeline }) {
  const Icone = ICONES[item.tipo]

  return (
    <ItemLinhaDoTempo
      marcador={<Icone className="h-4 w-4" />}
      tomMarcador={TOM_TIPO_INTERACAO[item.tipo]}
      titulo={ROTULO_TIPO_INTERACAO[item.tipo]}
      quando={formatarDataHora(item.ocorreuEm)}
      autor={item.autor ? `por ${item.autor.nome}` : 'registro do sistema'}
    >
      <div className="flex flex-col gap-2">
        {(item.statusAnterior || item.statusNovo) && item.statusAnterior !== item.statusNovo ? (
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            {item.statusAnterior ? (
              <Etiqueta tom="neutro">{ROTULO_STATUS_LEAD[item.statusAnterior]}</Etiqueta>
            ) : null}
            <span aria-hidden="true" className="text-conteudo-fraco">
              →
            </span>
            {item.statusNovo ? (
              <Etiqueta tom="marca">{ROTULO_STATUS_LEAD[item.statusNovo]}</Etiqueta>
            ) : null}
          </div>
        ) : null}

        {/*
          `TextoComLinks` e não o texto direto: a mensagem vem do servidor em texto
          puro e pode carregar endereços que a integração entregou (o imóvel, o
          histórico da conversa na Lais). Como `interacoes` é append-only por trigger,
          a mensagem gravada não pode ser reescrita — a apresentação amigável do link
          só é possível aqui, e por isso vale também para o que já está na base.
        */}
        {item.mensagem ? (
          <p className="whitespace-pre-wrap rounded-md bg-superficie-2 p-3 text-sm leading-relaxed text-conteudo">
            <TextoComLinks texto={item.mensagem} />
          </p>
        ) : null}

        {/* Bloco de transferência — o formato observado na TELA-05. */}
        {item.transferencia ? (
          <dl className="grid gap-x-4 gap-y-1 rounded-md bg-superficie-2 p-3 text-xs sm:grid-cols-2">
            <Par rotulo="Responsável anterior" valor={item.transferencia.responsavelAnterior?.nome ?? '— (pool)'} />
            <Par rotulo="Novo responsável" valor={item.transferencia.responsavelNovo?.nome ?? '— (pool)'} />
            <Par rotulo="Equipe antiga" valor={item.transferencia.equipeAnterior?.titulo ?? '—'} />
            <Par rotulo="Nova equipe" valor={item.transferencia.equipeNova?.titulo ?? '—'} />
          </dl>
        ) : null}

        <div className="flex flex-wrap gap-1.5">
          {item.motivoAgendamento ? (
            <Etiqueta tom="info" cor={null}>
              Motivo: {item.motivoAgendamento.titulo}
            </Etiqueta>
          ) : null}
          {item.motivoVendaNaoRealizada ? (
            <Etiqueta tom="perigo">Perda: {item.motivoVendaNaoRealizada.titulo}</Etiqueta>
          ) : null}
          {item.produtoAssociado ? (
            <Etiqueta tom="neutro">Produto: {item.produtoAssociado.titulo}</Etiqueta>
          ) : null}
        </div>

        {item.arquivos.length > 0 ? (
          <ul className="flex flex-col gap-1">
            {item.arquivos.map((arquivo) => (
              <li key={arquivo.uuid} className="flex items-center gap-1.5 text-xs text-conteudo-suave">
                <IconeArquivo className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{arquivo.nomeOriginal}</span>
                <span className="shrink-0 text-conteudo-fraco">
                  {(arquivo.tamanhoBytes / 1024).toFixed(0)} KB
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        {'payloadExterno' in item && item.payloadExterno ? (
          <PayloadIntegracao payload={item.payloadExterno} />
        ) : null}
      </div>
    </ItemLinhaDoTempo>
  )
}

function Par({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex gap-1.5">
      <dt className="font-semibold text-conteudo-suave">{rotulo}:</dt>
      <dd className="min-w-0 truncate text-conteudo">{valor}</dd>
    </div>
  )
}

/** Até onde o payload é aberto em pares antes de cair para JSON cru. */
const PROFUNDIDADE_MAXIMA = 4

/** Valor que precisa de linha inteira: objeto, ou lista que contenha objeto. */
function ehComposto(valor: unknown): boolean {
  if (Array.isArray(valor)) {
    return valor.some((item) => typeof item === 'object' && item !== null)
  }
  return typeof valor === 'object' && valor !== null
}

/**
 * Um valor do payload da integração, renderizado pelo que ele **é**.
 *
 * CORRIGE O CI-1 de `docs/release/13-ci.md §7`, que era visível ao usuário: até a
 * 13ª entrega esta posição era `String(valor ?? '—')` sobre o payload bruto, e o
 * payload que a API monta em `processador.service.ts#montarPayloadDaTimeline` tem
 * **quatro** chaves de valor composto —
 *
 *   formulario      { id, titulo, formularioExternoId }
 *   mapeamento      RelatorioMapeamento
 *   camposRecebidos [{ nome, valor }, …]   ← as respostas do formulário da Meta
 *   bruto           resposta crua da Graph API
 *
 * — então metade do painel imprimia literalmente `[object Object]`, inclusive a
 * linha que mais importa ao atendente: as respostas que o cliente preencheu.
 *
 * A correção **não** é `JSON.stringify`. Ela produziria
 * `[{"nome":"full_name","values":["Carla"]}]` numa célula de tabela de 12px —
 * legível para quem depura, ilegível para quem atende, e é a mesma informação que
 * o `<pre>` de fallback já oferece quando não há estrutura de pares.
 *
 * O que este componente faz é descer na estrutura: objeto vira sublista de pares,
 * lista de escalares vira enumeração, lista de objetos vira uma sublista por item.
 * Recursivo e sem palpite sobre formato — o que importa porque o formulário da Meta
 * é montado pelo marketing e pode trazer campo que ninguém previu (é a mesma razão
 * pela qual o mapeador da API guarda em `extras` o que não casa, RN-64).
 *
 * `PROFUNDIDADE_MAXIMA` fecha o caso patológico: abaixo dela o valor cai para JSON
 * indentado, que é honesto — nada é escondido, só deixa de ser tabulado.
 */
export function ValorDoPayload({
  valor,
  profundidade = 0,
}: {
  valor: unknown
  profundidade?: number
}) {
  if (valor === null || valor === undefined) {
    return <span className="text-conteudo-fraco">—</span>
  }

  if (typeof valor === 'string') {
    if (valor.trim().length === 0) {
      return <span className="text-conteudo-fraco">— (vazio)</span>
    }
    // `LinkExterno` centraliza `target`/`rel`/protocolo aceito — ver o componente. O
    // rótulo continua "abrir link" e **não** "Clique Aqui": aqui a chave técnica do
    // payload está impressa ao lado (`conversation_history: abrir link`), e o rótulo do
    // corpo da interação existe justamente porque lá a frase é que dá o contexto.
    return ehUrlExterna(valor) ? (
      <LinkExterno href={valor}>abrir link</LinkExterno>
    ) : (
      <>{valor}</>
    )
  }

  if (typeof valor === 'number') {
    return <>{valor.toLocaleString('pt-BR')}</>
  }

  // O painel é em português e lido por quem atende, não por quem depura: `true`
  // seria a grafia do JSON, não a da tela.
  if (typeof valor === 'boolean') {
    return <>{valor ? 'sim' : 'não'}</>
  }

  // Profundidade estourada, ou tipo que não é JSON (função, símbolo): JSON cru, que
  // não perde informação nenhuma.
  if (profundidade >= PROFUNDIDADE_MAXIMA || typeof valor !== 'object') {
    return (
      <pre className="overflow-x-auto whitespace-pre-wrap text-[0.6875rem] leading-tight">
        {JSON.stringify(valor, null, 2)}
      </pre>
    )
  }

  if (Array.isArray(valor)) {
    // `Array.isArray` sobre `unknown` estreita para `any[]`, e cada item viria como
    // `any` — que é exatamente o tipo que deixou o defeito passar da primeira vez.
    // A anotação devolve `unknown` a cada item e a recursão volta a ser conferida.
    const lista: ReadonlyArray<unknown> = valor

    if (lista.length === 0) {
      return <span className="text-conteudo-fraco">— (nenhum)</span>
    }

    // Lista só de escalares é uma enumeração, não uma estrutura: `a, b, c` diz mais
    // em uma linha do que três marcadores em três.
    if (!lista.some((item) => typeof item === 'object' && item !== null)) {
      return (
        <>
          {lista.map((item, indice) => (
            <span key={indice}>
              {indice > 0 ? ', ' : ''}
              <ValorDoPayload valor={item} profundidade={profundidade + 1} />
            </span>
          ))}
        </>
      )
    }

    return (
      <ol className="flex flex-col gap-1">
        {lista.map((item, indice) => (
          <li key={indice} className="border-l-2 border-borda pl-2">
            <ValorDoPayload valor={item} profundidade={profundidade + 1} />
          </li>
        ))}
      </ol>
    )
  }

  const entradas = Object.entries(valor as Record<string, unknown>)

  if (entradas.length === 0) {
    return <span className="text-conteudo-fraco">— (vazio)</span>
  }

  return <ParesDoPayload entradas={entradas} profundidade={profundidade} aninhado />
}

/**
 * Os pares chave → valor de um objeto do payload.
 *
 * A chave é mostrada **como veio**, sem tradução: `field_data`, `phone_number` e
 * `camposRecebidos` são os nomes que existem do outro lado, e é por eles que a
 * operação conversa com o marketing quando um campo chega errado. Traduzir só a
 * metade que é nossa produziria um painel que mistura dois vocabulários.
 */
function ParesDoPayload({
  entradas,
  profundidade,
  aninhado = false,
}: {
  entradas: ReadonlyArray<readonly [string, unknown]>
  profundidade: number
  aninhado?: boolean
}) {
  return (
    <dl
      className={cn(
        'grid gap-x-4 gap-y-1',
        aninhado ? 'grid-cols-1' : 'border-t border-borda px-3 py-2 text-xs sm:grid-cols-2',
      )}
    >
      {entradas.map(([chave, valor]) => (
        <div
          key={chave}
          className={cn('flex min-w-0 gap-1.5', !aninhado && ehComposto(valor) && 'sm:col-span-2')}
        >
          <dt className="shrink-0 font-semibold text-conteudo-suave">{chave}:</dt>
          <dd className="min-w-0 break-words text-conteudo">
            <ValorDoPayload valor={valor} profundidade={profundidade + 1} />
          </dd>
        </div>
      ))}
    </dl>
  )
}

/**
 * Payload bruto da integração (RN-64).
 *
 * A chave é **omitida** — não `null` — para quem não tem
 * `INTERACAO_LER_PAYLOAD_INTEGRACAO`, e é por isso que o teste é
 * `'payloadExterno' in item`: a tela precisa distinguir "não recebi permissão" de
 * "não existe payload".
 *
 * Recolhido por padrão: são as respostas do formulário do Meta, úteis ao atender,
 * mas com dezenas de campos técnicos que empurrariam o resto da timeline para
 * fora da tela.
 */
export function PayloadIntegracao({ payload }: { payload: unknown }) {
  const [aberto, setAberto] = useState(false)

  const pares =
    typeof payload === 'object' && payload !== null && !Array.isArray(payload)
      ? Object.entries(payload as Record<string, unknown>)
      : null

  return (
    <div className="rounded-md border border-borda bg-superficie-2">
      <button
        type="button"
        onClick={() => setAberto((valor) => !valor)}
        aria-expanded={aberto}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-xs font-semibold text-conteudo-suave hover:text-conteudo"
      >
        Dados recebidos da integração
        <span aria-hidden="true">{aberto ? '−' : '+'}</span>
      </button>

      {aberto ? (
        pares ? (
          <ParesDoPayload entradas={pares} profundidade={0} />
        ) : (
          // Payload que não é objeto de pares — array na raiz, ou escalar. Não é
          // formato que a integração produza hoje; se produzir, aparece inteiro.
          <pre className="overflow-x-auto border-t border-borda px-3 py-2 text-xs text-conteudo">
            {JSON.stringify(payload, null, 2)}
          </pre>
        )
      ) : null}
    </div>
  )
}
